import { useEffect, useState } from 'react';
import { __ } from '../functions/commom';
import SelectorBox from './base/SelectorBox';
import DateSelector from './base/DateSelector';
import moment from 'moment-timezone';
import InputBox from './base/InputBox';
import BlockSelector from './base/BlockSelector';
import CheckBox from './base/CheckBox';
import { useAppState } from '../state/StateContext';
import { DATE_FORMAT, MAX_RETURN_DAYS, MIN_ADVANCE_HOURS, TIMEZONE } from '../config';
import { createUseStyles } from 'react-jss';
import CouponCodeInput from './CouponCodeInput';

const useStyles = createUseStyles({
  SearchFormWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  planList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    '& .plan-item': {
      display: 'flex',
      gap: '5px',
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      cursor: 'pointer',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      transition: 'all 0.2s ease-in-out',
      '& .info': {
        padding: '15px',
        flex: 2,
        '& .title': {
          fontSize: '18px',
          fontWeight: 'bold',
          marginBottom: '10px',
          borderLeft: '5px solid var(--dai-primary-color)',
          paddingLeft: '10px',
        },
      },
      '& .apply_button': {
        backgroundColor: 'var(--dai-primary-color)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px',
        '& svg': {
          fill: '#ffffff',
          width: '20px',
          height: '20px',
        },
      },
      '& .price': {
        fontSize: '24px',
        fontWeight: 'bold',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--dai-primary-color)',
        gap: '5px',
        '& .symbol': {
          fontSize: '14px',
          color: '#666666',
        },
      },
      '&:hover': {
        color: '#fff',
        backgroundColor: 'var(--dai-primary-color)',
        '& .title': {
          borderColor: '#ffffff',
        },
        '& .price': {
          color: '#ffffff',
          '& .symbol': {
            color: '#ffffff',
          },
        },
      },
    },
  },
  planType: {
    fontSize: '14px',
  },
  planHighLight: {
    fontSize: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  planHighLightItem: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  dateRange: {
    display: 'flex',
    gap: '20px',
    '&> div': {
      flex: 1,
    },
  },
  '@media (max-width: 768px)': {
    dateRange: {
      flexDirection: 'column',
    },
  },
});

const SearchFormTravel: React.FC = () => {
  const classes = useStyles();
  const state = useAppState();

  const [errors, setErrors] = useState<
    Array<{ field: string; message: string }>
  >([]);

  const insurancePlans = state?.insurancePlans?.get();

  const selectedInsurance = Array.isArray(insurancePlans)
    ? insurancePlans.find(
        (plan) => plan.id === (state?.searchForm?.id?.get() || '')
      )
    : undefined;

  // Minimum date is 48 hours from now (MIN_ADVANCE_HOURS) using HKT
  const minBookingDate = moment().tz(TIMEZONE).add(MIN_ADVANCE_HOURS, 'hours').format(DATE_FORMAT);
  const nextYear = moment(minBookingDate).add(364, 'day').format(DATE_FORMAT);

  const departureDate = moment(state?.searchForm?.departureDate?.get()).format(
    DATE_FORMAT
  );
  const departureDatePlusOneYear = departureDate
    ? moment(departureDate).add(364, 'day').format(DATE_FORMAT)
    : '';

  function setErrorMessage(field: string, message: string) {
    setErrors((prevErrors) =>
      prevErrors.map((error) =>
        error.field === field ? { ...error, message } : error
      )
    );
  }

  function handleIdChange(value: string | undefined) {
    state?.searchForm?.id?.set(value || '');
  }

  function handleCoverageTypeChange(value: string | 'individual' | 'family') {
    state?.searchForm?.coverageType?.set(value || 'individual');
  }

  function handleSearchDepartureDateChange(date: string | null) {
    if (!date) return;
    const returnDate = state.searchForm.returnDate.get();

    // If return date is before departure date, reset return date
    if (returnDate && moment(returnDate).isBefore(date)) {
      state.searchForm.returnDate.set(null);
      setErrorMessage('returnDate', 'Return date must be after departure date');
    }

    state.searchForm.departureDate.set(date);
  }

  const maxreturnDate = moment(state.searchForm.departureDate.get() || minBookingDate)
    .add(MAX_RETURN_DAYS - 1, 'days')
    .format(DATE_FORMAT);

  const handleSearchReturnDateChange = (date: string | null) => {
    if (!date) return;

    if (date < (state?.searchForm?.departureDate?.get() || minBookingDate)) {
      setErrorMessage('returnDate', 'Return date must be after departure date');
    }

    state.searchForm.returnDate.set(date);
  };

  const handleWithCruiseChange = (value: boolean) => {
    state?.searchForm?.withCruise?.set(value);
  };

  // Ensure departure date is at least minBookingDate (48 hours in advance)
  useEffect(() => {
    const currentDepartureDate = state?.searchForm?.departureDate?.get();
    
    // If no departure date set or if it's before minBookingDate, set to minBookingDate
    if (!currentDepartureDate || moment(currentDepartureDate).isBefore(moment(minBookingDate))) {
      state?.searchForm?.departureDate?.set(minBookingDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minBookingDate]);

  useEffect(() => {
    if (selectedInsurance?.isAnnualPlan) {
      state?.searchForm?.coverageType?.set('individual');
      state?.searchForm?.withCruise?.set(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInsurance?.isAnnualPlan]);

  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     state?.searchForm?.couponCode?.set(couponCodeState);
  //   }, 500);
  //   return () => clearTimeout(timeoutId);
  // }, [couponCodeState]);

  return (
    <>
      <div className={classes.SearchFormWrapper}>
        <SelectorBox
          label={__('Plan')}
          options={
            Array.isArray(insurancePlans)
              ? insurancePlans
                  .filter((plan) => plan.category === 'travel')
                  .map((plan) => ({
                    label: plan?.name || '',
                    value: plan?.id || '',
                  }))
              : []
          }
          value={state?.searchForm?.id?.get() || ''}
          onChange={(value) => handleIdChange(value)}
        />

        <div className={classes.dateRange}>
          <DateSelector
            label={
              state?.searchForm?.type?.get() === 'annual'
                ? __('Effective Date')
                : __('Departure Date')
            }
            value={
              state?.searchForm?.departureDate?.get()
                ? moment(state?.searchForm?.departureDate?.get()).format(
                    DATE_FORMAT
                  )
                : null
            }
            onChange={handleSearchDepartureDateChange}
            minDate={minBookingDate}
            maxDate={nextYear}
            required
          />
          {selectedInsurance?.isAnnualPlan ? (
            <InputBox
              label={__('Return Date')}
              value={departureDatePlusOneYear}
              placeholder={__('Please select Departure Date first')}
              disabled={true}
            />
          ) : (
            <DateSelector
              label={__('Return Date')}
              placeholder={__('Select Return Date')}
              value={
                state?.searchForm?.returnDate?.get()
                  ? moment(state?.searchForm?.returnDate?.get()).format(
                      DATE_FORMAT
                    )
                  : null
              }
              onChange={handleSearchReturnDateChange}
              minDate={
                state?.searchForm?.departureDate?.get()
                  ? moment(state?.searchForm?.departureDate?.get()).format(
                      DATE_FORMAT
                    )
                  : minBookingDate
              }
              maxDate={maxreturnDate}
              errorMessage={
                errors?.find((error) => error.field === 'returnDate')?.message
              }
              required={
                selectedInsurance ? !selectedInsurance.isAnnualPlan : true
              }
            />
          )}
        </div>

        {selectedInsurance?.types && (
          <BlockSelector
            label={__('Coverage')}
            options={selectedInsurance.types.map((type: string) => ({
              label: __(type || ''),
              value: type || '',
            }))}
            value={
              selectedInsurance?.types?.length > 1
                ? state?.searchForm?.coverageType?.get() || 'individual'
                : 'individual'
            }
            callback={handleCoverageTypeChange}
            gap={20}
          />
        )}

        {selectedInsurance?.id === 'single_trip' && (
          <CheckBox
            label={__('With Cruise extension')}
            checked={state?.searchForm?.withCruise?.get() || false}
            onChange={handleWithCruiseChange}
          />
        )}

        <hr />

        <CouponCodeInput />
      </div>
    </>
  );
};

export default SearchFormTravel;
